// React error boundary with branded fallback. Two intended placements:
//
//   1. Top-level (around <App />) — catches catastrophic crashes that
//      escape route boundaries. Fallback is a full-page screen with a
//      "Reload" action that does a hard `location.reload()`.
//   2. Route-level (around <Outlet /> in Layout) — keeps the header,
//      nav, and theme/auth state usable when a single page throws.
//      Pass `resetKey={location.pathname}` so navigating to a different
//      route automatically clears the boundary.
//
// Class component because hooks cannot catch render errors. The
// fallback inlines its brand styling rather than importing shared
// components — one of those imports could be the thing that just broke.

import { Component, type ErrorInfo, type ReactNode } from "react";
import ErrorBoundaryFallback from "./ErrorBoundaryFallback";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** When the value of this prop changes, the boundary resets. Pass
   *  the current route pathname for route-level boundaries so a
   *  one-off crash on one page does not wedge the user on every other
   *  page. Omit on top-level boundaries — those should require an
   *  explicit reload. */
  resetKey?: string;
  /** Override the rendered fallback UI. When omitted, the boundary
   *  renders the default branded screen. The override receives the
   *  caught error and a reset callback. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** Optional label for the default fallback ("dashboard", "this
   *  contract", etc.) so the user knows which scope failed. */
  scopeLabel?: string;
  /** Forwarded to `componentDidCatch` for custom telemetry. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export default class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidUpdate(prev: ErrorBoundaryProps): void {
    if (
      this.state.error &&
      this.props.resetKey !== undefined &&
      prev.resetKey !== this.props.resetKey
    ) {
      this.reset();
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Always log so the developer console shows something on prod
    // crashes — the browser stack trace is the only hint a non-Sentry
    // user will get. Production minification turns the stack into
    // mangled identifiers; the message and componentStack are still
    // readable enough to triage.
    console.error("ErrorBoundary caught:", error, info.componentStack);
    if (this.props.onError) this.props.onError(error, info);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) {
      return this.props.fallback(this.state.error, this.reset);
    }
    return (
      <ErrorBoundaryFallback
        error={this.state.error}
        scopeLabel={this.props.scopeLabel}
        onReset={this.reset}
      />
    );
  }
}
